import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type {
  AdminEnvelope,
  AdminRoleTypesRegistry,
  AdminRoleTypesRegistryParams,
  AssignUserRolePayload,
  RoleTypePayload,
  UserAccountRole,
  UserAccountRoleType,
} from '@/client/types/admin/users-orgs';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import useGetMutationFn from '../use-get-mutation-fn';
import useGetQueryFn from '../use-get-query-fn';
import { toFormData } from './lib/form';
import { useClientStore } from '@/client/stores/client';
import { useUserStore } from '@/client/stores/user';
import { withErrorHandling } from '@/client/utils/errors';

export const adminRoleKeys = {
  all: ['admin', 'roles'] as const,
  roleTypes: () => [...adminRoleKeys.all, 'role-types'] as const,
  roleTypeRegistry: (params: AdminRoleTypesRegistryParams) =>
    [...adminRoleKeys.all, 'role-types', 'registry', params] as const,
  userRoles: (authid: string | null) =>
    [...adminRoleKeys.all, 'user-roles', authid] as const,
};

/**
 * Coerce a role-endpoint payload to an array.
 */
const toArray = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const items = Object.values(data as Record<string, unknown>).find(
      Array.isArray
    );
    if (items) return items as T[];
  }
  return [];
};

/* ────────────────────── Role-type registry ────────────────────── */

/**
 * `GET /api/admin/role_type` — flattened to the full array, for dropdowns and
 * role-name lookup maps. The backend now paginates (default 10), so we request
 * the max page (server caps page_size at 200) to keep the pickers complete.
 */
export const useRoleTypesQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<UserAccountRoleType[]>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching role types',
    endpoint: '/api/admin/role_type',
    paginated: true,
  });

  return useQuery({
    queryKey: adminRoleKeys.roleTypes(),
    queryFn: () => queryFn({ page: 1, page_size: 200 }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => toArray<UserAccountRoleType>(response?.data),
  });
};

/**
 * `GET /api/admin/role_type` with server-side `search` / `sort_by` /
 * `sort_order` / pagination — for the registry table. Returns the items plus
 * the totals the pager needs.
 */
export const useRoleTypeRegistryQuery = (
  params: AdminRoleTypesRegistryParams = {}
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminRoleTypesRegistry>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching role types',
    endpoint: '/api/admin/role_type',
    paginated: true,
  });

  return useQuery({
    queryKey: adminRoleKeys.roleTypeRegistry(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response): AdminRoleTypesRegistry => {
      const data = response?.data as Partial<AdminRoleTypesRegistry> | undefined;
      const role_types = toArray<UserAccountRoleType>(data);
      return {
        role_types,
        page: data?.page ?? params.page ?? 1,
        page_size: data?.page_size ?? params.page_size ?? 10,
        total: data?.total ?? role_types.length,
        total_pages: data?.total_pages ?? 1,
      };
    },
  });
};

/** `POST /api/admin/role_type` — FORM-ENCODED (gap C-1). Not `/role_type/add`. */
export const useCreateRoleTypeMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    FormData,
    AdminEnvelope<UserAccountRoleType>
  >({
    defaultErrorMessage: 'An unexpected error occurred while creating the role type',
    endpoint: '/api/admin/role_type',
    method: 'POST',
  });

  return useMutation({
    mutationFn: (payload: RoleTypePayload) =>
      mutationFn(toFormData(payload as unknown as Record<string, unknown>)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminRoleKeys.roleTypes() });
    },
  });
};

export const useUpdateRoleTypeMutation = () => {
  const queryClient = useQueryClient();
  const request = useClientStore((state) => state.request);

  return useMutation({
    mutationFn: withErrorHandling(
      ({ uartid, ...rest }: RoleTypePayload & { uartid: number }) =>
        request<AdminEnvelope<UserAccountRoleType>>(
          `/api/admin/role_type/${uartid}`,
          'PUT',
          toFormData(rest as unknown as Record<string, unknown>)
        ),
      'An unexpected error occurred while updating the role type'
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminRoleKeys.roleTypes() });
    },
  });
};

/** `DELETE /api/admin/role_type/<uartid>`. */
export const useDeleteRoleTypeMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { uartid: number },
    AdminEnvelope<{ message: string }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while deleting the role type',
    endpoint: (payload) => `/api/admin/role_type/${payload.uartid}`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminRoleKeys.roleTypes() });
    },
  });
};

/* ────────────────────── Role assignments ────────────────────── */

/**
 * `GET /api/admin/user_role/<authid>` — assignments for one user.
 */
export const useUserRoleAssignmentsQuery = (
  authid: string | null,
  enabled = true
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<UserAccountRole[]>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching user roles',
    endpoint: `/api/admin/user_role/${authid}`,
  });

  return useQuery({
    queryKey: adminRoleKeys.userRoles(authid),
    queryFn: () => queryFn({}),
    enabled: Boolean(authid) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => toArray<UserAccountRole>(response?.data),
  });
};

/**
 * `POST /api/admin/user_role` — JSON. Assign one or more role types.
 *
 * Both former quirks are fixed in ff-api 344ae714: the response now reports
 * every role created (not just the last), and an all-already-assigned request
 * is a clean no-op instead of a 500. The body is
 * `{ assigned: [...], skipped: [uartid], invalid: [uartid] }`; the refetch below
 * still drives the UI, so read `skipped`/`invalid` only if you want to surface
 * why a requested role produced no new row.
 */
export const useAssignUserRoleMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    AssignUserRolePayload,
    AdminEnvelope<UserAccountRole>
  >({
    defaultErrorMessage: 'An unexpected error occurred while assigning the role',
    endpoint: '/api/admin/user_role',
    method: 'POST',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminRoleKeys.all });
      void useUserStore.getState().refreshCachedUser();
    },
  });
};

/** `DELETE /api/admin/user_role/<uarid>` — revoke an assignment. */
export const useRevokeUserRoleMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<{ uarid: number }, unknown>({
    defaultErrorMessage: 'An unexpected error occurred while revoking the role',
    endpoint: (payload) => `/api/admin/user_role/${payload.uarid}`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminRoleKeys.all });
      void useUserStore.getState().refreshCachedUser();
    },
  });
};

/**
 * Update assignment notes — `PUT /api/admin/user_role/<uarid>`.
 *
 * Form-encoded (gap C-1). Sends the FormData directly as the body (same fix as
 * `useUpdateRoleTypeMutation` — wrapping it in `{ uarid, form }` made the
 * fields vanish under JSON.stringify). The backend handler used to call its
 * async update without `await` — returning 200 having written nothing — which
 * is fixed in ff-api 344ae714, so a 200 now means the row really changed.
 */
export const useUpdateUserRoleMutation = () => {
  const queryClient = useQueryClient();
  const request = useClientStore((state) => state.request);

  return useMutation({
    mutationFn: withErrorHandling(
      ({ uarid, ...patch }: { uarid: number } & Partial<UserAccountRole>) =>
        request<AdminEnvelope<UserAccountRole>>(
          `/api/admin/user_role/${uarid}`,
          'PUT',
          toFormData(patch as unknown as Record<string, unknown>)
        ),
      'An unexpected error occurred while updating the role'
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminRoleKeys.all });
    },
  });
};
